import React, { useState, useCallback } from 'react';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ImageUpload = ({ 
  currentImage, 
  onImageUploaded, 
  folder = 'products',
  className = '' 
}) => {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(currentImage || '');

  const uploadToCloudinary = async (file) => {
    try {
      setUploading(true);
      
      // Get signature from backend
      const sigResponse = await fetch(`${API_URL}/api/cloudinary/signature?folder=${folder}&resource_type=image`);
      if (!sigResponse.ok) {
        throw new Error('Failed to get upload signature');
      }
      const sig = await sigResponse.json();
      
      // Upload to Cloudinary
      const formData = new FormData();
      formData.append('file', file);
      formData.append('api_key', sig.api_key);
      formData.append('timestamp', sig.timestamp);
      formData.append('signature', sig.signature);
      formData.append('folder', sig.folder);
      
      const uploadResponse = await fetch(
        `https://api.cloudinary.com/v1_1/${sig.cloud_name}/image/upload`,
        { method: 'POST', body: formData }
      );
      
      if (!uploadResponse.ok) {
        throw new Error('Upload failed');
      }
      
      const result = await uploadResponse.json();
      
      // Use the optimized URL with auto format and quality
      const optimizedUrl = result.secure_url.replace('/upload/', '/upload/q_auto,f_auto/');
      
      setPreviewUrl(optimizedUrl);
      onImageUploaded(optimizedUrl, result.public_id);
      toast.success('Image uploaded successfully');
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      validateAndUpload(file);
    }
  };

  const validateAndUpload = (file) => {
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid file type. Please use JPG, PNG, WebP, or GIF');
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }
    
    uploadToCloudinary(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      validateAndUpload(file);
    }
  };

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleRemoveImage = () => {
    setPreviewUrl('');
    onImageUploaded('', '');
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="text-sm font-medium text-gray-700">Product Image</label>
      
      <div
        className={`
          relative border-2 border-dashed rounded-lg transition-colors
          ${dragOver ? 'border-amber-500 bg-amber-50' : 'border-gray-300 hover:border-gray-400'}
          ${previewUrl ? 'p-2' : 'p-6'}
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
      >
        {uploading ? (
          <div className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-10 w-10 text-amber-500 animate-spin" />
            <p className="mt-2 text-sm text-gray-600">Uploading...</p>
          </div>
        ) : previewUrl ? (
          <div className="relative">
            <img
              src={previewUrl}
              alt="Product preview"
              className="w-full h-48 object-contain rounded-lg bg-gray-50"
            />
            <Button
              type="button"
              variant="destructive"
              size="sm"
              className="absolute top-2 right-2 h-8 w-8 p-0"
              onClick={handleRemoveImage}
            >
              <X className="h-4 w-4" />
            </Button>
            <label className="absolute bottom-2 right-2 cursor-pointer">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/gif"
                onChange={handleFileSelect}
                className="hidden"
              />
              <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium bg-white/90 hover:bg-white rounded-md shadow-sm border">
                <Upload className="h-3 w-3 mr-1" />
                Replace
              </span>
            </label>
          </div>
        ) : (
          <label className="cursor-pointer block">
            <input
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={handleFileSelect}
              className="hidden"
            />
            <div className="flex flex-col items-center">
              <div className="p-3 bg-gray-100 rounded-full">
                <ImageIcon className="h-8 w-8 text-gray-400" />
              </div>
              <p className="mt-3 text-sm text-gray-600">
                <span className="font-medium text-amber-600 hover:text-amber-700">
                  Click to upload
                </span>
                {' '}or drag and drop
              </p>
              <p className="mt-1 text-xs text-gray-500">
                JPG, PNG, WebP or GIF (max 5MB)
              </p>
            </div>
          </label>
        )}
      </div>
    </div>
  );
};

export default ImageUpload;
